import type { SwapContactsModalProps } from '../../types/modals'
import { useState, type ReactNode } from 'react'
import {
  ArrowLeftRight,
  Mail,
  Phone,
  Smartphone,
  Users,
} from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { swapContacts } from '../../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { formatPhoneNumber } from '../../utils/applicationFormatters'
import modalStyles from '../../styles/modals.module.css'

const tid = 'application-modal-swap-contacts'

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
]

function getInitials(first?: string, last?: string): string {
  const firstInitial = (first ?? '').trim().charAt(0)
  const lastInitial = (last ?? '').trim().charAt(0)
  const initials = `${firstInitial}${lastInitial}`.toUpperCase()
  return initials || '?'
}

function StepBadge({ step }: { step: number }) {
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
      {step}
    </span>
  )
}

function SwapTypeCard({
  checked,
  onChange,
  icon,
  title,
  description,
  testId,
}: {
  checked: boolean
  onChange: () => void
  icon: ReactNode
  title: string
  description: string
  testId: string
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition ${
        checked
          ? 'border-blue-500 bg-blue-50/60'
          : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50'
      }`}
      data-testid={`${testId}-label`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-blue-600"
        checked={checked}
        onChange={onChange}
        data-testid={`${testId}-input`}
      />
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-1 block text-sm text-slate-500">{description}</span>
      </span>
    </label>
  )
}

export function SwapContactsModal({
  applicantId,
  applicationAllData,
  onClose,
  onSuccess,
}: SwapContactsModalProps) {
  const [selectedApplicants, setSelectedApplicants] = useState<string[]>([])
  const [swapTypeEmail, setSwapTypeEmail] = useState(false)
  const [swapTypeMobile, setSwapTypeMobile] = useState(false)
  const [loading, setLoading] = useState(false)

  const coresidents = (
    applicationAllData?.co_resident_application
      ? Object.values(applicationAllData.co_resident_application)
      : []
  ) as Array<{
    applicant_id?: string | number
    user_data?: {
      fk_user_id?: string | number
      mobile_no?: string
      first_name?: string
      last_name?: string
      email?: string
    }
  }>

  const handleApplicantToggle = (userId: string) => {
    setSelectedApplicants((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId)
      }
      if (prev.length < 2) {
        return [...prev, userId]
      }
      return prev
    })
  }

  const handleSubmit = async () => {
    if (selectedApplicants.length !== 2) {
      showError('Please select exactly two applicants to swap')
      return
    }

    if (!swapTypeEmail && !swapTypeMobile) {
      showError('Please select at least one swap type (Email or Mobile)')
      return
    }

    setLoading(true)
    try {
      await swapContacts(applicantId, {
        swap_applicants: selectedApplicants,
        swap_type_email: swapTypeEmail,
        swap_type_mobile: swapTypeMobile,
      })
      showSuccess('Applicant Contacts Info Swap Successfully!')
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : 'Failed to swap contacts')
    } finally {
      setLoading(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={true}
      title="Swap Contact Info"
      onClose={onClose}
      maxWidth="max-w-2xl"
      headerVariant="iconTitle"
      titleIcon={<Users className="h-[18px] w-[18px]" />}
      footerAlign="end"
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      bodyScrollOnly
      footer={
        <div
          className={`${modalStyles.modalFooterActionGroup} ${modalStyles.modalFooterActionGroupEnd}`}
          data-testid={`${tid}-footer`}
        >
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            onClick={onClose}
            data-testid={`${tid}-cancel-btn`}
          >
            Cancel
          </button>
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            onClick={handleSubmit}
            disabled={loading || selectedApplicants.length !== 2}
            data-testid={`${tid}-submit-button`}
          >
            <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
            {loading ? 'Processing...' : 'Swap Contacts'}
          </button>
        </div>
      }
    >
      <div className="space-y-6 px-1 py-1">
        <section data-testid={`${tid}-applicants-section`}>
          <div className="mb-3 flex items-start gap-3">
            <StepBadge step={1} />
            <div>
              <h3
                className="text-sm font-semibold text-slate-900"
                data-testid={`${tid}-applicants-heading`}
              >
                Select two applicants to swap contact info
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Choose exactly two applicants from the list below.
              </p>
            </div>
          </div>

          <div
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
            data-testid={`${tid}-applicants-list`}
          >
            {coresidents.map((coresident, index) => {
              const userId = String(coresident?.user_data?.fk_user_id ?? '')
              const firstName = coresident?.user_data?.first_name ?? ''
              const lastName = coresident?.user_data?.last_name ?? ''
              const email = coresident?.user_data?.email ?? ''
              const phone = coresident?.user_data?.mobile_no
                ? formatPhoneNumber(coresident.user_data.mobile_no)
                : ''
              const isSelected = selectedApplicants.includes(userId)
              const isDisabled = !isSelected && selectedApplicants.length >= 2
              const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length]

              return (
                <label
                  key={coresident.applicant_id}
                  data-testid={`${tid}-row-${coresident.applicant_id}-label`}
                  className={`flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : ''
                  } ${isSelected ? 'bg-blue-50/40' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 accent-blue-600"
                    checked={isSelected}
                    onChange={() => handleApplicantToggle(userId)}
                    disabled={isDisabled}
                    data-testid={`${tid}-row-${coresident.applicant_id}-input`}
                  />
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${avatarColor}`}
                    aria-hidden="true"
                  >
                    {getInitials(firstName, lastName)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-900">
                      {[firstName, lastName].filter(Boolean).join(' ')}
                    </span>
                    {email ? (
                      <span className="mt-0.5 block truncate text-sm text-slate-500">
                        {email}
                      </span>
                    ) : null}
                  </span>
                  {phone ? (
                    <span className="flex shrink-0 items-center gap-1.5 text-sm text-slate-600">
                      <Phone className="h-4 w-4 text-blue-600" aria-hidden="true" />
                      {phone}
                    </span>
                  ) : null}
                </label>
              )
            })}
          </div>
        </section>

        <section data-testid={`${tid}-swap-type-section`}>
          <div className="mb-3 flex items-start gap-3">
            <StepBadge step={2} />
            <div>
              <h3
                className="text-sm font-semibold text-slate-900"
                data-testid={`${tid}-swap-type-heading`}
              >
                Select swap type
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Choose how you want to swap the contact information.
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <SwapTypeCard
              checked={swapTypeEmail}
              onChange={() => setSwapTypeEmail((value) => !value)}
              icon={<Mail className="h-5 w-5" aria-hidden="true" />}
              title="Email"
              description="Swap email addresses between selected applicants."
              testId={`${tid}-email`}
            />
            <SwapTypeCard
              checked={swapTypeMobile}
              onChange={() => setSwapTypeMobile((value) => !value)}
              icon={<Smartphone className="h-5 w-5" aria-hidden="true" />}
              title="Mobile"
              description="Swap mobile numbers between selected applicants."
              testId={`${tid}-mobile`}
            />
          </div>
        </section>
      </div>
    </OnScreenModal>
  )
}
